import {type StyleProp, View, type ViewStyle} from 'react-native'

import {atoms as a} from '#/alf'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {EarthFilledIcon} from './EarthFilledIcon'
import {WMarkIcon} from './WMarkIcon'

/**
 * Placeholder shown wherever a trend has no image (`heroImage: null`). The
 * backend only ever derives `heroImage` from an article card's image, so the
 * glyph doubles as a hint about where the trend comes from:
 *
 *  - `globe`  - the trend has article cards but none carried a usable image
 *              (news-driven, picture unavailable).
 *  - `brand`  - the trend has no article cards at all: a native W Social
 *              conversation, so the full-colour W mark is the honest
 *              placeholder.
 *
 * Callers pick the variant from `trend.featuredCards.length`. The wire
 * `topPosts` carry no media, so there is nothing else to fall back to.
 *
 * Matches the imageless treatment the article cards use
 * (`FeaturedCardsCarousel`): a rounded tile centred on the image area, so an
 * empty slot reads as "no image" rather than a blank block.
 *
 * `tileSize` scales the placeholder for smaller image areas. The globe sits in
 * a tile of that size (icon half the tile, corner radius a quarter); the brand
 * mark has no tile and is drawn at the full `tileSize` instead.
 */
export function TrendImageFallback({
  style,
  tileSize = 64,
  variant = 'globe',
}: {
  style?: StyleProp<ViewStyle>
  tileSize?: number
  /** `globe` when the trend has article cards, `brand` when it has none. */
  variant?: 'globe' | 'brand'
}) {
  const ss = useSemanticTokens()
  return (
    <View
      style={[
        a.align_center,
        a.justify_center,
        {backgroundColor: ss.bg.contrast_50},
        style,
      ]}>
      {variant === 'brand' ? (
        // The mark has its own shape and colour, so it needs no tile - it takes
        // the tile's full footprint instead of half of it.
        <WMarkIcon width={tileSize} />
      ) : (
        <View
          style={[
            a.align_center,
            a.justify_center,
            {
              width: tileSize,
              height: tileSize,
              borderRadius: tileSize / 4,
              backgroundColor: ss.bg.contrast_25,
            },
          ]}>
          <EarthFilledIcon width={Math.round(tileSize / 2)} />
        </View>
      )}
    </View>
  )
}
